import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { useMatch } from '@gatsbyjs/reach-router';

import { displayModal, hideModal } from '@store/appStateSlice';
import AnalyticsLink from '../Analytics/AnalyticsLink';
import MultiVehicleNav from '../MultiVehicleNav';
import styles from './PortalHeader.module.scss';
import content from './PortalHeader.json';
import { getLocalizedContent as l } from '@helpers/languages';
import { useAnalytics } from '@utils';

export const isBrowser = () => typeof window !== 'undefined';

if (isBrowser()) {
  window.portalHeaderScrollPosition = 0;
}

const PortalHeader = ({ isLoggedIn, location }) => {
  const { isProfileLoading, user, language, direction, showModal } =
    useSelector(({ appState, userProfile }) => ({
      ...appState,
      ...userProfile,
    }));
  const {
    vehicles,
    activeVehicle: { index: activeVehicleIndex },
  } = user;
  const hasVehicles = !!vehicles?.length;

  const [isNavShowing, setIsNavShowing] = useState(true);
  const [isDropdownShowing, setIsDropdownShowing] = useState(false);
  const [scrollDirection, setScrollDirection] = useState();
  const dispatch = useDispatch();
  const { trackInteraction } = useAnalytics();

  const isActive = ({ isPartiallyCurrent, href, location }) =>
    isPartiallyCurrent || href.indexOf(location.pathname) > -1
      ? { className: [styles.navLink, styles.active].join(' ') }
      : { className: styles.navLink };

  const tabText = l(content.reservationsTab, language);

  // used to hide navigation on confirmation page
  const match = useMatch('/portal/confirmation');

  useEffect(() => {
    setIsDropdownShowing(false);
    document.body.classList.remove('lock');
    innerWidth < 991 && dispatch(hideModal());
  }, [activeVehicleIndex]);

  // eslint-disable-next-line no-unused-vars
  const animate = () => {
    handleScroll();
    requestAnimationFrame(animate);
  };

  const handleScroll = () => {
    if (!isBrowser()) {
      return;
    }
    const { top, bottom } = document.body.getBoundingClientRect();
    const bodyHeight = bottom - top;

    const overscrollBottom =
      Math.floor(bodyHeight - window.innerHeight) <= Math.floor(Math.abs(top));
    const overscrollTop = top > -1;

    if (overscrollTop || overscrollBottom) {
      return;
    }
    if (top > window.portalHeaderScrollPosition) {
      setScrollDirection('down');
    } else {
      setScrollDirection('up');
    }
    window.portalHeaderScrollPosition = top;
  };

  useEffect(() => {
    setIsNavShowing(true);
  }, [location.pathname]);

  useEffect(() => {
    if (scrollDirection === 'down') {
      setIsNavShowing(true);
    } else if (scrollDirection === 'up') {
      setIsNavShowing(false);
    }
  }, [scrollDirection]);

  const handleDrop = newShowDrop => {
    if (newShowDrop) {
      const { innerWidth } = window;
      innerWidth < 991 &&
        dispatch(
          displayModal({
            modalName: 'VEHICLE_SELECTOR',
          })
        );
    }
    trackDropdownToggle();
    setIsDropdownShowing(newShowDrop);
  };

  const onClickOutsideDropdown = event => {
    if (!document.getElementById('vehicle-menu')?.contains(event.target)) {
      setIsDropdownShowing(false);
    }
  };

  useEffect(() => {
    if (showModal === false && innerWidth < 991 && isDropdownShowing) {
      setIsDropdownShowing(false);
    }
  }, [showModal]);

  const trackDropdownToggle = useCallback(() => {
    if (vehicles?.[activeVehicleIndex]?.status) {
      trackInteraction('cta', vehicles[activeVehicleIndex].status);
    }
  }, [vehicles, activeVehicleIndex]);

  useEffect(() => {
    if (isDropdownShowing) {
      document.body.classList.add('lock');
      if (innerWidth >= 991) {
        document.body.addEventListener('click', onClickOutsideDropdown);
      }
    } else {
      document.body.classList.remove('lock');
      if (innerWidth >= 991) {
        document.body.removeEventListener('click', onClickOutsideDropdown);
      }
    }
    return () => {
      document.body.classList.remove('lock');
      document.body.removeEventListener('click', onClickOutsideDropdown);
    };
  }, [isDropdownShowing]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const gaProps = {
    gaType: 'cta',
    gaComponent: 'navigation',
  };

  return (
    <div className={isNavShowing ? styles.portalWrap : styles.hidden}>
      <AnimatePresence>
        <motion.div
          layout
          className={[
            styles.PortalHeader,
            isLoggedIn === false && styles.isLoggedOut,
            showModal === true,
          ].join(' ')}
          key="page-header"
          initial={{ height: '0px' }}
          animate={
            isLoggedIn && !match ? { height: '105px' } : { height: '0px' }
          }
          exit={{ height: '0px' }}
          transition={{ duration: 0.5 }}
          style={{ paddingTop: isLoggedIn && !hasVehicles ? '1rem' : '0' }}
        >
          {!isProfileLoading && !match ? (
            <div className={styles.container}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                {hasVehicles && (
                  <MultiVehicleNav
                    toggleDropdown={handleDrop}
                    isDropdownShowing={isDropdownShowing}
                    inModal={false}
                  />
                )}
                <div
                  className={[
                    styles.portalNav,
                    styles[language],
                    styles[direction],
                  ].join(' ')}
                >
                  {hasVehicles && (
                    <AnalyticsLink
                      to={`/portal/dashboard`}
                      getProps={isActive}
                      {...gaProps}
                      gaText="dashboard"
                    >
                      {tabText}
                    </AnalyticsLink>
                  )}
                  <AnalyticsLink
                    to={`/portal/designs`}
                    getProps={isActive}
                    {...gaProps}
                    gaText="designs"
                  >
                    {l(content.savedDesignsTab, language)}
                  </AnalyticsLink>
                  <AnalyticsLink
                    to={`/portal/account`}
                    getProps={isActive}
                    {...gaProps}
                    gaText="account"
                  >
                    {l(content.accountInfoTab, language)}
                  </AnalyticsLink>
                </div>
              </motion.div>
            </div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PortalHeader;
